/**
 * useLeaderboard — paginated points ranking (POST /UserProfile/LeaderBoardList).
 *
 * `startIndex` is a 1-based row offset, so each page advances it by pageSize.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import profileService from '../api/services/profileService';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types/api';
import type {
  LeaderBoardCurrentUser,
  LeaderBoardItem,
} from '../types/userProfile';

const DEFAULT_PAGE_SIZE = 20;

export interface UseLeaderboardResult {
  items: LeaderBoardItem[];
  currentUser: LeaderBoardCurrentUser | null;
  totalCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string;
  loaded: boolean;
  refresh: () => void;
  loadMore: () => void;
}

export const useLeaderboard = (
  pageSize: number = DEFAULT_PAGE_SIZE,
): UseLeaderboardResult => {
  const [items, setItems] = useState<LeaderBoardItem[]>([]);
  const [currentUser, setCurrentUser] = useState<LeaderBoardCurrentUser | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const requestIdRef = useRef(0);
  const startIndexRef = useRef(1);

  const load = useCallback(
    async (startIndex: number, mode: 'replace' | 'append') => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (mode === 'append') setLoadingMore(true);
      else setLoading(true);
      setError('');

      try {
        const response = await profileService.getLeaderBoardList({
          startIndex,
          pageSize,
        });
        if (requestId !== requestIdRef.current) return;

        const data = response?.data;
        if (data?.success === false) {
          setError(data?.message || '');
          if (mode === 'replace') setItems([]);
          setHasMore(false);
          return;
        }

        const received = Array.isArray(data?.leaderBoard) ? data.leaderBoard : [];
        const total = Number(data?.totalCount) || 0;

        setItems(previous => {
          const next =
            mode === 'replace'
              ? received
              : [
                  ...previous,
                  ...received.filter(
                    item => !previous.some(existing => existing.userId === item.userId),
                  ),
                ];
          setHasMore(received.length === pageSize && next.length < total);
          return next;
        });
        setCurrentUser(data?.currentUser ?? null);
        setTotalCount(total);
        startIndexRef.current = startIndex;
      } catch (caught) {
        if (requestId !== requestIdRef.current) return;
        setError(getApiErrorMessage(caught as ApiError));
        if (mode === 'replace') setItems([]);
        setHasMore(false);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
          setLoaded(true);
        }
      }
    },
    [pageSize],
  );

  useEffect(() => {
    startIndexRef.current = 1;
    load(1, 'replace').catch(() => undefined);
  }, [load, reloadToken]);

  const refresh = useCallback(() => setReloadToken(token => token + 1), []);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    load(startIndexRef.current + pageSize, 'append').catch(() => undefined);
  }, [hasMore, load, loading, loadingMore, pageSize]);

  return {
    items,
    currentUser,
    totalCount,
    loading,
    loadingMore,
    hasMore,
    error,
    loaded,
    refresh,
    loadMore,
  };
};

export default useLeaderboard;
